<script lang="ts" setup>
    import type { MessageFeedback } from 'models'
    import { computed } from 'vue'

    const props = defineProps<{
        feedback: MessageFeedback
    }>()

    // The real reaction glyph, shown ONLY for unmapped reactions (no vote):
    // 👍/❤️/👎 keep the classic thumb icon, while e.g. WhatsApp "😍" shows the
    // actual emoji.
    const emoji = computed(() => {
        if (props.feedback.vote) {
            return undefined
        }
        const raw = props.feedback.metadata?.reactionEmoji
        return typeof raw === 'string' && /\p{Extended_Pictographic}/u.test(raw)
            ? raw
            : undefined
    })

    // Accent matches the vote; neutral for a real emoji (it carries its own
    // colour) or a comment-only feedback.
    const toneClass = computed(() => {
        if (emoji.value) {
            return 'pk-message-feedback--neutral'
        }
        switch (props.feedback.vote) {
            case 'upvote':
                return 'pk-message-feedback--positive'
            case 'downvote':
                return 'pk-message-feedback--negative'
            default:
                return 'pk-message-feedback--neutral'
        }
    })

    // Fallback icon when there is no real emoji glyph to show.
    const icon = computed(() => {
        switch (props.feedback.vote) {
            case 'upvote':
                return 'ri:thumb-up-fill'
            case 'downvote':
                return 'ri:thumb-down-fill'
            default:
                return 'ri:chat-quote-line'
        }
    })
</script>

<template>
    <div class="pk-message-feedback" :class="toneClass">
        <span class="pk-message-feedback__badge">
            <span v-if="emoji" class="pk-message-feedback__emoji">
                {{ emoji }}
            </span>
            <VvIcon v-else :name="icon" />
        </span>
        <p v-if="feedback.comment" class="pk-message-feedback__comment">
            {{ feedback.comment }}
        </p>
    </div>
</template>

<style lang="scss">
    /*
     * The user's reaction + comment, shown as an annotation hanging off the
     * assistant message: echoes the bubble radius (same speech-bubble tail) and
     * a coloured left rail + badge match the vote. Sits flush under the bubble.
     */
    .pk-message-feedback {
        display: flex;
        align-items: center;
        gap: var(--spacing-8);
        padding: var(--spacing-6) var(--spacing-12) var(--spacing-6)
            var(--spacing-6);
        border-radius: var(--rounded-lg);
        background: var(--pk-feedback-surface);
        font-size: var(--text-12);
        color: var(--color-word-2);

        // The accent colors the thumb on the surface: the text shades of
        // success and danger, the fills where a host app loads only the
        // library and this package's stylesheet
        &--positive {
            --pk-feedback-accent: var(--u-success-text, var(--color-success));
        }

        &--negative {
            --pk-feedback-accent: var(--u-danger-text, var(--color-danger));
        }

        &--neutral {
            --pk-feedback-accent: var(--color-surface-4);
        }

        --pk-feedback-surface: color-mix(
            in srgb,
            var(--pk-feedback-accent) 8%,
            var(--color-surface)
        );

        &__badge {
            display: inline-flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            width: var(--spacing-22, 1.375rem);
            height: var(--spacing-22, 1.375rem);
            border-radius: var(--rounded-full, 9999px);
            background: var(--color-surface);
            color: var(--pk-feedback-accent);
            box-shadow: var(--shadow-sm);
        }

        &__emoji {
            /* Smaller than the badge so the glyph doesn't touch the ring. */
            font-size: var(--text-12);
            line-height: 1;
        }

        &__comment {
            margin: 0;

            /* Preserve the user's line breaks; avoid orphans on wrap. */
            white-space: pre-wrap;
            text-wrap: pretty;
        }
    }
</style>
